"use client";

import { useState, useRef } from "react";
import { Input } from "../../ui/input";
import { Button } from "../../ui/button";
import { toast } from "sonner";
import { post } from "../../../lib/api";
import { readFileWithEncodingDetection } from "../../../lib/encoding-utils";

const SpellingBeeGenerator = ({ game_id }) => {
  const [fileContent, setFileContent] = useState("");
  const [centerLetter, setCenterLetter] = useState("");
  const [allowedLetters, setAllowedLetters] = useState([]);
  const [validWords, setValidWords] = useState([]);
  const [puzzleName, setPuzzleName] = useState("");

  const fileInputRef = useRef(null);

  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || file.type !== "text/plain") {
      toast.error("Please upload a valid .txt file.");
      return;
    }

    // Set puzzleName to filename without extension
    const nameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
    setPuzzleName(nameWithoutExt);

    try {
      const text = await readFileWithEncodingDetection(file);
      setFileContent(text);
      parseSpellingBeeFile(text);
    } catch (error) {
      toast.error("Failed to read file. Please try again.");
      console.error("File reading error:", error);
    }
  };

  const parseSpellingBeeFile = (text) => {
    try {
      const lines = text.split("\n").map((line) => line.trim());
      if (lines.length < 2) throw new Error("Invalid file format.");

      const lettersLine = lines[0].replace(/\s+/g, "").toUpperCase();
      setCenterLetter(lettersLine[0]);
      setAllowedLetters(lettersLine.split(""));

      const words = lines.slice(1).filter(Boolean);
      setValidWords(words);
      toast.success("File parsed successfully!");
    } catch (err) {
      toast.error("Failed to parse file. Please check formatting.");
      console.error(err);
    }
  };

  const formatLetters = () => {
    const others = allowedLetters.filter((l) => l !== centerLetter);
    return `${centerLetter}|${others.join(",")}`;
  };

  const handlePostPuzzle = async () => {
    if (!puzzleName.trim()) {
      toast.error("Please enter a puzzle name.");
      return;
    }

    if (!centerLetter || allowedLetters.length === 0 || validWords.length === 0) {
      toast.error("Incomplete puzzle data.");
      return;
    }

    const puzzleData = [
      {
        variant_id: 1,
        variant_name: "letters",
        data: formatLetters(),
      },
      {
        variant_id: 2,
        variant_name: "words",
        data: validWords.join(","),
      },
    ];

    try {
      for (const variant of puzzleData) {
        await post(
          {
            method: "addChallengeNoDup",
            game_id: game_id,
            challenge_id: puzzleName,
            variant_id: variant.variant_id,
            variant_name: variant.variant_name,
            data: variant.data,
          },
          "/challenges"
        );
      }

      toast.success("Spelling Bee puzzle uploaded!");
      //setPuzzleName("");
      /*setFileContent("");
      setCenterLetter("");
      setAllowedLetters([]);
      setValidWords([]);*/
    } catch (err) {
      toast.error("Upload failed!");
      console.error(err);
    }
  };

  return (
    <div className="space-y-4 p-4 border rounded-md">
      {/* Hidden native file input */}
      <input
        type="file"
        accept=".txt"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileUpload}
      />

      {/* Custom button to trigger file select */}
      <button
        type="button"
        className="bg-purple-800 hover:bg-purple-950 text-white px-4 py-2 rounded"
        onClick={triggerFileSelect}
      >
        Upload Spelling Bee txt file
      </button>

      <Input
        placeholder="Enter puzzle name"
        value={puzzleName}
        onChange={(e) => setPuzzleName(e.target.value)}
      />

      <textarea
        className="w-full p-2 border rounded bg-gray-100 text-black max-h-60 overflow-auto whitespace-pre-wrap text-sm"
        rows={10}
        value={fileContent}
        onChange={(e) => setFileContent(e.target.value)}
        placeholder="Preview will appear here..."
      />

      <Button
        className="bg-purple-800 hover:bg-purple-950 text-white"
        onClick={handlePostPuzzle}
      >
        Upload Spelling Bee Puzzle
      </Button>
    </div>
  );
};

export default SpellingBeeGenerator;
